// Единая точка форматирования дат/времени в SPA.
//
// Зачем модуль: на один Telegram-аккаунт заходит несколько саппортов, и у
// каждого свой часовой пояс на устройстве. Раньше каждый компонент звал
// `new Intl.DateTimeFormat("ru-RU", …)` БЕЗ timeZone — время рендерилось в
// поясе браузера, а «сегодня/вчера» считались по локальной полуночи. У
// саппорта вне МСК это давало сдвиг и на времени, и на границах суток.
//
// Теперь все пользовательские даты идут через эти функции и принимают
// `tz` — предпочтение из ui-prefs ("auto" = как на устройстве, иначе IANA-имя
// вроде "Europe/Moscow"). Значение всегда прокидывается ЯВНО, чтобы React
// перерисовывал ленту при смене пояса (компонент подписан на ui-prefs).

export const TIME_ZONE_AUTO = "auto";

/** "auto" | IANA-идентификатор ("Europe/Moscow"). */
export type TimeZonePref = string;

export interface TimeZoneOption {
  readonly value: string;
  readonly label: string;
}

// Список для селекта в настройках. Держим коротким и «рабочим»: пояса, из
// которых реально заходят саппорты. Всегда IANA-имена — они учитывают
// исторические переводы часов, в отличие от голого UTC±N.
export const TIME_ZONE_OPTIONS: readonly TimeZoneOption[] = [
  { value: TIME_ZONE_AUTO, label: "Как на устройстве" },
  { value: "Europe/Kaliningrad", label: "Калининград (UTC+2)" },
  { value: "Europe/Moscow", label: "Москва (UTC+3)" },
  { value: "Europe/Kyiv", label: "Киев (UTC+2/+3)" },
  { value: "Europe/Minsk", label: "Минск (UTC+3)" },
  { value: "Europe/Chisinau", label: "Кишинёв (UTC+2/+3)" },
  { value: "Europe/Riga", label: "Рига (UTC+2/+3)" },
  { value: "Europe/Warsaw", label: "Варшава (UTC+1/+2)" },
  { value: "Europe/Berlin", label: "Берлин (UTC+1/+2)" },
  { value: "Europe/London", label: "Лондон (UTC+0/+1)" },
  { value: "Europe/Lisbon", label: "Лиссабон (UTC+0/+1)" },
  { value: "Europe/Istanbul", label: "Стамбул (UTC+3)" },
  { value: "Asia/Tbilisi", label: "Тбилиси (UTC+4)" },
  { value: "Asia/Yerevan", label: "Ереван (UTC+4)" },
  { value: "Asia/Baku", label: "Баку (UTC+4)" },
  { value: "Asia/Dubai", label: "Дубай (UTC+4)" },
  { value: "Asia/Yekaterinburg", label: "Екатеринбург (UTC+5)" },
  { value: "Asia/Tashkent", label: "Ташкент (UTC+5)" },
  { value: "Asia/Almaty", label: "Алматы (UTC+5)" },
  { value: "Asia/Novosibirsk", label: "Новосибирск (UTC+7)" },
  { value: "Asia/Bangkok", label: "Бангкок (UTC+7)" },
  { value: "Asia/Krasnoyarsk", label: "Красноярск (UTC+7)" },
  { value: "Asia/Irkutsk", label: "Иркутск (UTC+8)" },
  { value: "Asia/Vladivostok", label: "Владивосток (UTC+10)" },
  { value: "America/New_York", label: "Нью-Йорк (UTC−5/−4)" },
  { value: "America/Los_Angeles", label: "Лос-Анджелес (UTC−8/−7)" },
  { value: "UTC", label: "UTC" }
];

export const isKnownTimeZone = (value: string): boolean =>
  TIME_ZONE_OPTIONS.some((option) => option.value === value);

// Свежие tzdb-переименования: старые ICU (Safari <16, Chrome <106, WebView)
// не знают новый id и Intl кидает RangeError. Раньше это тихо откатывалось
// на пояс УСТРОЙСТВА (и кэшировалось!) — юзер выбирал Киев, а видел чужой
// пояс без единого намёка. Теперь сначала пробуем легаси-алиас.
const ZONE_ALIASES: Record<string, string> = {
  "Europe/Kyiv": "Europe/Kiev"
};

// Результат проверки «понимает ли движок этот пояс» — по паре (pref → id).
const resolvedZoneCache = new Map<string, string | undefined>();

const zoneSupportedByEngine = (zone: string): boolean => {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: zone });
    return true;
  } catch {
    return false;
  }
};

/** "auto" → undefined (Intl возьмёт пояс устройства). */
const resolveZone = (pref: TimeZonePref | undefined): string | undefined => {
  if (!pref || pref === TIME_ZONE_AUTO) {
    return undefined;
  }
  if (resolvedZoneCache.has(pref)) {
    return resolvedZoneCache.get(pref);
  }
  let resolved: string | undefined;
  const alias = ZONE_ALIASES[pref];
  if (zoneSupportedByEngine(pref)) {
    resolved = pref;
  } else if (alias && zoneSupportedByEngine(alias)) {
    resolved = alias;
  } else {
    resolved = undefined;
  }
  resolvedZoneCache.set(pref, resolved);
  return resolved;
};

// Конструктор Intl дорогой, а форматтеры зовутся на каждую видимую строку
// виртуализированных списков — кэшируем по (набор опций × пояс).
const formatterCache = new Map<string, Intl.DateTimeFormat>();

const getFormatter = (
  cacheKey: string,
  locale: string,
  options: Intl.DateTimeFormatOptions,
  pref: TimeZonePref | undefined
): Intl.DateTimeFormat => {
  const zone = resolveZone(pref);
  const key = `${locale}|${cacheKey}|${zone ?? ""}`;
  const cached = formatterCache.get(key);
  if (cached) {
    return cached;
  }
  let formatter: Intl.DateTimeFormat;
  try {
    formatter = new Intl.DateTimeFormat(
      locale,
      zone ? { ...options, timeZone: zone } : options
    );
  } catch {
    // Неизвестный движку пояс (старый браузер, опечатка в localStorage) —
    // не роняем рендер, падаем на пояс устройства.
    formatter = new Intl.DateTimeFormat(locale, options);
  }
  formatterCache.set(key, formatter);
  return formatter;
};

const toDate = (value: string | number | Date): Date | null => {
  const date = value instanceof Date ? value : new Date(value);
  return Number.isFinite(date.getTime()) ? date : null;
};

/** «14:32». */
export const formatTime = (value: string, tz?: TimeZonePref): string => {
  const date = toDate(value);
  if (!date) return "";
  return getFormatter("hm", "ru-RU", { hour: "2-digit", minute: "2-digit" }, tz).format(date);
};

/** «29.07 14:32» — компактная отметка для служебных строк. */
export const formatShortDateTime = (value: string, tz?: TimeZonePref): string => {
  const date = toDate(value);
  if (!date) return "";
  return getFormatter(
    "ddmmhm",
    "ru-RU",
    { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" },
    tz
  ).format(date);
};

/** «29.07.2026, 14:32» — для тултипов и админских списков. */
export const formatFullDateTime = (value: string, tz?: TimeZonePref): string => {
  const date = toDate(value);
  if (!date) return "";
  return getFormatter(
    "full",
    "ru-RU",
    {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    },
    tz
  ).format(date);
};

/**
 * «10 авг. 2026 г., 17:36:27» — подсказка на времени сообщения.
 * Секунды здесь не украшение: саппорты выдерживают паузы между отправками и
 * ориентируются именно по ним, поэтому формат совпадает с оригинальным
 * Telegram (дата + точное время).
 */
export const formatExactDateTime = (value: string, tz?: TimeZonePref): string => {
  const date = toDate(value);
  if (!date) return "";
  return getFormatter(
    "exact",
    "ru-RU",
    {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit"
    },
    tz
  ).format(date);
};

/** «29 июля» / «29 июля 2025 г.» */
export const formatDate = (
  value: string,
  tz?: TimeZonePref,
  withYear = false
): string => {
  const date = toDate(value);
  if (!date) return "";
  return getFormatter(
    withYear ? "dmy" : "dm",
    "ru-RU",
    withYear
      ? { day: "numeric", month: "long", year: "numeric" }
      : { day: "numeric", month: "long" },
    tz
  ).format(date);
};

// ---------------------------------------------------------------------------
// Календарные сутки в выбранном поясе
// ---------------------------------------------------------------------------

// dayKey зовётся 3× на каждую видимую строку ленты (сама, соседи сверху и
// снизу) на КАЖДЫЙ кадр скролла, а formatToParts не бесплатен. ISO-строки
// сообщений стабильны — мемоизируем по (пояс, значение).
const dayKeyCache = new Map<string, string>();
const DAY_KEY_CACHE_LIMIT = 20_000;

// Ключ суток "YYYY-MM-DD" В ВЫБРАННОМ ПОЯСЕ. Сравнение таких ключей —
// единственный корректный способ спросить «это один и тот же день?»: считать
// через getFullYear()/setHours() нельзя, они работают в поясе устройства.
export const dayKey = (value: string | number | Date, tz?: TimeZonePref): string => {
  const cacheKey = typeof value === "string" ? `${tz ?? ""}|${value}` : null;
  if (cacheKey !== null) {
    const cached = dayKeyCache.get(cacheKey);
    if (cached !== undefined) {
      return cached;
    }
  }
  const date = toDate(value);
  if (!date) return "";
  const parts = getFormatter(
    "daykey",
    "en-CA",
    { year: "numeric", month: "2-digit", day: "2-digit" },
    tz
  ).formatToParts(date);
  const year = parts.find((part) => part.type === "year")?.value ?? "";
  const month = parts.find((part) => part.type === "month")?.value ?? "";
  const day = parts.find((part) => part.type === "day")?.value ?? "";
  const key = `${year}-${month}-${day}`;
  if (cacheKey !== null) {
    if (dayKeyCache.size >= DAY_KEY_CACHE_LIMIT) {
      dayKeyCache.clear();
    }
    dayKeyCache.set(cacheKey, key);
  }
  return key;
};

/** Сдвиг ключа суток на N дней. Арифметика в UTC — переводы часов не мешают. */
export const shiftDayKey = (key: string, days: number): string => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key);
  if (!match) return key;
  const shifted = new Date(
    Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])) + days * 86_400_000
  );
  const pad = (value: number): string => value.toString().padStart(2, "0");
  return `${shifted.getUTCFullYear()}-${pad(shifted.getUTCMonth() + 1)}-${pad(
    shifted.getUTCDate()
  )}`;
};

/** Сегодняшняя дата «YYYY-MM-DD» в выбранном поясе (для <input type="date">). */
// Кэшируем по (пояс, минута): dayKey с number-входом идёт мимо dayKeyCache, и
// без этого кэша каждая видимая строка списка диалогов дергала formatToParts
// на каждый кадр скролла (todayDayKey зовут formatDialogTime/formatPeerStatus).
let todayDayKeyCache: { tz: string; minute: number; value: string } | null = null;
export const todayDayKey = (tz?: TimeZonePref): string => {
  const now = Date.now();
  const minute = Math.floor(now / 60_000);
  const tzKey = tz ?? "";
  if (todayDayKeyCache && todayDayKeyCache.tz === tzKey && todayDayKeyCache.minute === minute) {
    return todayDayKeyCache.value;
  }
  const value = dayKey(now, tz);
  todayDayKeyCache = { tz: tzKey, minute, value };
  return value;
};

/**
 * Подпись разделителя суток в ленте, как в Telegram:
 * «Сегодня» / «Вчера» / «29 июля» / «29 июля 2025 г.» (для прошлых лет).
 */
export const formatDaySeparator = (value: string, tz?: TimeZonePref): string => {
  const date = toDate(value);
  if (!date) return "";
  const key = dayKey(date, tz);
  const today = todayDayKey(tz);
  if (key === today) return "Сегодня";
  if (key === shiftDayKey(today, -1)) return "Вчера";
  const sameYear = key.slice(0, 4) === today.slice(0, 4);
  return formatDate(date.toISOString(), tz, !sameYear);
};

/**
 * Время в строке списка диалогов: сегодня — «14:32», последняя неделя —
 * «Пн», старше — «12.05.26». Границы суток считаются в выбранном поясе.
 */
export const formatDialogTime = (value: string, tz?: TimeZonePref): string => {
  const date = toDate(value);
  if (!date || date.getTime() <= 0) {
    return "";
  }
  const key = dayKey(date, tz);
  const today = todayDayKey(tz);
  if (key === today) {
    return formatTime(value, tz);
  }
  if (key >= shiftDayKey(today, -6)) {
    const weekday = getFormatter("wd", "ru-RU", { weekday: "short" }, tz).format(date);
    return weekday.charAt(0).toUpperCase() + weekday.slice(1);
  }
  return getFormatter(
    "dmy2",
    "ru-RU",
    { day: "2-digit", month: "2-digit", year: "2-digit" },
    tz
  ).format(date);
};

/**
 * Строка «был(а) в сети» для шапки чата и профиля. Момент приходит с воркера
 * как ISO (raw), а формат выбирается здесь — в поясе саппорта:
 * сегодня → «был(а) сегодня в 14:32», вчера → «был(а) вчера в 14:32»,
 * раньше → «был(а) 29.07 в 14:32».
 */
export const formatPeerStatus = (
  lastSeenAt: string | undefined,
  fallbackLabel: string | undefined,
  tz?: TimeZonePref
): string | null => {
  if (!lastSeenAt) {
    return fallbackLabel ?? null;
  }
  const date = toDate(lastSeenAt);
  if (!date) {
    return fallbackLabel ?? null;
  }
  const key = dayKey(date, tz);
  const today = todayDayKey(tz);
  const time = formatTime(lastSeenAt, tz);
  if (key === today) {
    return `был(а) сегодня в ${time}`;
  }
  if (key === shiftDayKey(today, -1)) {
    return `был(а) вчера в ${time}`;
  }
  const sameYear = key.slice(0, 4) === today.slice(0, 4);
  const dateText = getFormatter(
    sameYear ? "ddmm" : "ddmmyy",
    "ru-RU",
    sameYear
      ? { day: "2-digit", month: "2-digit" }
      : { day: "2-digit", month: "2-digit", year: "2-digit" },
    tz
  ).format(date);
  return `был(а) ${dateText} в ${time}`;
};

// ---------------------------------------------------------------------------
// Смещение пояса и «стенные часы» (для отложенной отправки)
// ---------------------------------------------------------------------------

/** Смещение пояса в минутах к востоку от UTC на конкретный момент. */
const zoneOffsetMinutes = (utcMs: number, tz?: TimeZonePref): number => {
  const zone = resolveZone(tz);
  if (!zone) {
    return -new Date(utcMs).getTimezoneOffset();
  }
  const parts = getFormatter(
    "offsetprobe",
    "en-US",
    {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false
    },
    tz
  ).formatToParts(new Date(utcMs));
  const pick = (type: string): number =>
    Number(parts.find((part) => part.type === type)?.value ?? "0");
  // hour12:false в некоторых движках даёт 24 вместо 0 для полуночи.
  const hour = pick("hour") % 24;
  const asIfUtc = Date.UTC(
    pick("year"),
    pick("month") - 1,
    pick("day"),
    hour,
    pick("minute"),
    pick("second")
  );
  return Math.round((asIfUtc - Math.floor(utcMs / 1000) * 1000) / 60_000);
};

/**
 * Смещение выбранного пояса от UTC в минутах на заданный момент. Нужно там,
 * где фильтр по «времени суток» считает не SPA, а сервер (автонабор чатов в
 * папку): форматирование остаётся здесь, наружу уходит одно число.
 */
export const timeZoneOffsetMinutes = (tz?: TimeZonePref, at?: number): number =>
  zoneOffsetMinutes(at ?? Date.now(), tz);

/** «GMT+3» → «UTC+3»: короткая подпись пояса для подсказок в UI. */
export const timeZoneShortLabel = (tz?: TimeZonePref): string => {
  const offset = zoneOffsetMinutes(Date.now(), tz);
  const sign = offset < 0 ? "−" : "+";
  const abs = Math.abs(offset);
  const hours = Math.floor(abs / 60);
  const minutes = abs % 60;
  return `UTC${sign}${hours}${minutes ? `:${minutes.toString().padStart(2, "0")}` : ""}`;
};

/**
 * «YYYY-MM-DDTHH:mm», прочитанное как стенные часы ВЫБРАННОГО пояса → UTC ISO.
 * Две итерации: около перевода часов смещение до/после сдвига различается.
 */
export const wallClockToUtcIso = (wall: string, tz?: TimeZonePref): string | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(wall);
  if (!match) return null;
  const naiveUtc = Date.UTC(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
    Number(match[4]),
    Number(match[5])
  );
  let utcMs = naiveUtc - zoneOffsetMinutes(naiveUtc, tz) * 60_000;
  utcMs = naiveUtc - zoneOffsetMinutes(utcMs, tz) * 60_000;
  return Number.isFinite(utcMs) ? new Date(utcMs).toISOString() : null;
};

/** Обратное преобразование: момент времени → «YYYY-MM-DDTHH:mm» в поясе. */
export const utcToWallClock = (
  value: string | number | Date,
  tz?: TimeZonePref
): string => {
  const date = toDate(value);
  if (!date) return "";
  const parts = getFormatter(
    "wallclock",
    "en-CA",
    {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false
    },
    tz
  ).formatToParts(date);
  const pick = (type: string): string =>
    parts.find((part) => part.type === type)?.value ?? "00";
  const hour = (Number(pick("hour")) % 24).toString().padStart(2, "0");
  return `${pick("year")}-${pick("month")}-${pick("day")}T${hour}:${pick("minute")}`;
};
